import { Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';

interface ChallengeTarget {
    id: number;
    target_type: { value: string; label: string };
    requirement_name: string;
    required_amount: number;
    current_amount: number;
    remaining_amount: number;
    progress_percent: number;
    is_complete: boolean;
}

interface ChallengeData {
    id: number;
    name: string;
    description: string | null;
    status: { value: string; label: string };
    enclave: {
        id: number;
        name: string;
        owner_name: string | null;
    } | null;
    progress_percent: number;
    completed_at: string | null;
    awards_distributed_at: string | null;
    targets: ChallengeTarget[];
    reward_bands: Array<{
        id: number;
        label: string | null;
        rank_from: number;
        rank_to: number | null;
        rewards: Array<{
            reward_type: string;
            chance_percent?: number;
            item_recipe_id?: number | null;
            material_type?: string | null;
            quantity_min?: number | null;
            quantity_max?: number | null;
            reputation_min?: number | null;
            reputation_max?: number | null;
            enclave_id?: number | null;
        }>;
    }>;
}

interface LeaderboardRow {
    user_id: number;
    username: string;
    total: number;
    rank: number;
    reputation: number;
    materials: number;
    items: number;
    quests: number;
}

interface ContributionEntry {
    id: number;
    user: { id: number; username: string } | null;
    contribution_type: string;
    amount: number;
    created_at: string | null;
}

interface AwardEntry {
    id: number;
    user: { id: number; username: string } | null;
    rank: number | null;
    total_contribution: number;
    rewards_granted: Array<Record<string, unknown>>;
    awarded_at: string | null;
}

interface Props {
    challenge: ChallengeData;
    leaderboard: LeaderboardRow[];
    recentContributions: ContributionEntry[];
    awards: AwardEntry[];
}

export default function Show({ challenge, leaderboard, recentContributions, awards }: Props) {
    return (
        <GmLayout title={challenge.name} activePath="/gm/global-challenges">
            <div className="max-w-7xl space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white">{challenge.name}</h1>
                        <p className="text-sm text-gray-400 mt-1">
                            Enclave: {challenge.enclave?.owner_name || challenge.enclave?.name || 'Unknown'}
                        </p>
                    </div>
                    <Link href="/gm/global-challenges" className="text-sm text-gray-300 hover:text-white">
                        ← Back to Challenges
                    </Link>
                </div>

                <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                        <div>
                            <p className="text-gray-400">Status</p>
                            <p className="text-white font-medium">{challenge.status.label}</p>
                        </div>
                        <div>
                            <p className="text-gray-400">Progress</p>
                            <p className="text-white font-medium">{challenge.progress_percent}%</p>
                        </div>
                        <div>
                            <p className="text-gray-400">Completed At</p>
                            <p className="text-white font-medium">{challenge.completed_at ?? 'Not completed'}</p>
                        </div>
                        <div>
                            <p className="text-gray-400">Awards</p>
                            <p className="text-white font-medium">{challenge.awards_distributed_at ? 'Distributed' : 'Pending'}</p>
                        </div>
                    </div>
                    {challenge.description && (
                        <p className="mt-4 text-gray-200 whitespace-pre-wrap">{challenge.description}</p>
                    )}
                </div>

                <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                    <h2 className="text-lg font-semibold text-white mb-4">Targets</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-left text-xs text-gray-400 uppercase">
                                    <th className="pb-2">Type</th>
                                    <th className="pb-2">Requirement</th>
                                    <th className="pb-2">Progress</th>
                                    <th className="pb-2">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700">
                                {challenge.targets.map((target) => (
                                    <tr key={target.id}>
                                        <td className="py-3 text-sm text-gray-300">{target.target_type.label}</td>
                                        <td className="py-3 text-sm text-gray-300">{target.requirement_name}</td>
                                        <td className="py-3 text-sm text-gray-200">
                                            {target.current_amount.toLocaleString()} / {target.required_amount.toLocaleString()} ({target.progress_percent}%)
                                        </td>
                                        <td className="py-3 text-sm">
                                            <span className={`px-2 py-1 rounded text-xs ${target.is_complete ? 'bg-green-600 text-white' : 'bg-slate-600 text-gray-200'}`}>
                                                {target.is_complete ? 'Complete' : 'In Progress'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-white mb-4">Leaderboard</h2>
                        {leaderboard.length === 0 ? (
                            <p className="text-sm text-gray-400">No contributions yet.</p>
                        ) : (
                            <div className="space-y-2">
                                {leaderboard.map((row) => (
                                    <div key={row.user_id} className="flex items-center justify-between p-3 bg-slate-700 rounded">
                                        <div>
                                            <p className="text-sm text-white">#{row.rank} {row.username}</p>
                                            <p className="text-xs text-gray-300">
                                                Rep {row.reputation} · Mats {row.materials} · Items {row.items} · Quests {row.quests}
                                            </p>
                                        </div>
                                        <div className="text-sm text-purple-300 font-medium">{row.total.toLocaleString()}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-white mb-4">Reward Bands</h2>
                        {challenge.reward_bands.length === 0 ? (
                            <p className="text-sm text-gray-400">No reward bands configured.</p>
                        ) : (
                            <div className="space-y-3">
                                {challenge.reward_bands.map((band) => (
                                    <div key={band.id} className="p-3 bg-slate-700 rounded">
                                        <p className="text-sm text-white font-medium">
                                            {band.label || `Ranks ${band.rank_from}${band.rank_to ? `-${band.rank_to}` : '+'}`}
                                        </p>
                                        <p className="text-xs text-gray-300 mb-2">
                                            Rank range: {band.rank_from}{band.rank_to ? `-${band.rank_to}` : '+'}
                                        </p>
                                        {band.rewards.length === 0 ? (
                                            <p className="text-xs text-gray-400">No rewards configured.</p>
                                        ) : (
                                            <ul className="space-y-1 text-xs text-gray-200">
                                                {band.rewards.map((reward, index) => (
                                                    <li key={`${band.id}-reward-${index}`}>
                                                        {reward.reward_type}
                                                        {reward.material_type ? ` · ${reward.material_type}` : ''}
                                                        {reward.quantity_min !== undefined && reward.quantity_min !== null ? ` · qty ${reward.quantity_min}` : ''}
                                                        {reward.reputation_min !== undefined && reward.reputation_min !== null ? ` · rep ${reward.reputation_min}` : ''}
                                                        {reward.chance_percent ? ` · ${reward.chance_percent}%` : ''}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-white mb-4">Recent Contributions</h2>
                        {recentContributions.length === 0 ? (
                            <p className="text-sm text-gray-400">No contribution entries yet.</p>
                        ) : (
                            <div className="space-y-2 max-h-80 overflow-y-auto">
                                {recentContributions.map((entry) => (
                                    <div key={entry.id} className="p-2 bg-slate-700 rounded text-sm text-gray-200 flex items-center justify-between">
                                        <span>{entry.user?.username || 'Unknown'} · {entry.contribution_type}</span>
                                        <span className="text-purple-300">+{entry.amount}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-white mb-4">Award Records</h2>
                        {awards.length === 0 ? (
                            <p className="text-sm text-gray-400">No awards have been recorded yet.</p>
                        ) : (
                            <div className="space-y-2 max-h-80 overflow-y-auto">
                                {awards.map((award) => (
                                    <div key={award.id} className="p-3 bg-slate-700 rounded">
                                        <p className="text-sm text-white">
                                            #{award.rank ?? '-'} {award.user?.username || 'Unknown'}
                                        </p>
                                        <p className="text-xs text-gray-300">Contribution: {award.total_contribution}</p>
                                        <p className="text-xs text-gray-300">Rewards: {award.rewards_granted.length}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </GmLayout>
    );
}
